import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { userAPI, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Pagination from '../../components/UI/Pagination';
import toast from 'react-hot-toast';

const Users = () => {
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    search: '',
    page: 1,
    limit: 10,
  });

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    ['users', filters],
    () => userAPI.getUsers(filters),
    {
      keepPreviousData: true,
    }
  );
console.log(data);
  const users = data?.data?.data?.users || [];
  const pagination = data?.data?.data?.pagination;
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Create / actions state
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', full_name: '', password: '', role: 'staff' });
  const [formErrors, setFormErrors] = useState({});

  const submitCreate = async (e) => {
    e.preventDefault();
    // Client-side validation
    const errors = {};
    if (!form.full_name?.trim()) errors.full_name = 'Full name is required';
    if (!form.username?.trim()) errors.username = 'Username is required';
    if (!form.email?.trim()) errors.email = 'Email is required';
    else if (!/^([^\s@]+)@([^\s@]+)\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email address';
    if (!form.password || form.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setCreating(true);
    try {
      await userAPI.createUser(form);
      setShowCreate(false);
      setForm({ username: '', email: '', full_name: '', password: '', role: 'staff' });
      setFormErrors({});
      // Refresh all users lists regardless of filters
      queryClient.invalidateQueries(['users']);
      toast.success('User created successfully');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreate = () => {
    setShowCreate(true);
    setFormErrors({});
  };

  const handleCreateError = (error) => {
    const resp = error?.response?.data;
    if (Array.isArray(resp?.errors)) {
      const newErrors = {};
      resp.errors.forEach((err) => {
        if (err?.field && err?.message) newErrors[err.field] = err.message;
      });
      setFormErrors(newErrors);
      Object.entries(newErrors).forEach(([field, message]) => {
        toast.error(`${field}: ${message}`);
      });
    } else {
      const message = resp?.message || 'Failed to create user';
      // If server says duplicate username/email, try to map to specific fields
      if (message.toLowerCase().includes('email')) {
        setFormErrors((prev) => ({ ...prev, email: message }));
      }
      if (message.toLowerCase().includes('username')) {
        setFormErrors((prev) => ({ ...prev, username: message }));
      }
      toast.error(message);
    }
  };

  const toggleStatus = async (user) => {
    await userAPI.updateUserStatus(user.id, { is_active: !user.is_active });
    queryClient.invalidateQueries(['users']);
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Delete user ${user.full_name}?`)) return;
    await userAPI.deleteUser(user.id);
    queryClient.invalidateQueries(['users']);
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading users: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage system users and permissions
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              id="role"
              value={filters.role}
              onChange={(e) => handleFilterChange({ role: e.target.value })}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange({ status: e.target.value })}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700">
              Search
            </label>
            <input
              type="text"
              id="search"
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Search users..."
            />
          </div>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-primary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                        No users found.
                      </td>
                    </tr>
                  )}
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-primary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-600">
                                {user.full_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              @{user.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-red-100 text-red-800'
                            : user.role === 'manager'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login ? formatDate(user.last_login) : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => toggleStatus(user)}
                          className={`px-3 py-1 rounded border ${user.is_active ? 'text-yellow-700 border-yellow-300 hover:bg-yellow-50' : 'text-green-700 border-green-300 hover:bg-green-50'}`}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => removeUser(user)}
                          className="px-3 py-1 rounded border text-red-700 border-red-300 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
            <CreateUserModal
              open={showCreate}
              onClose={() => setShowCreate(false)}
              form={form}
              setForm={setForm}
              onSubmit={submitCreate}
              loading={creating}
              formErrors={formErrors}
              onCreateError={handleCreateError}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Users;

// Create User Modal (inline simple modal)
function CreateUserModal({ open, onClose, form, setForm, onSubmit, loading, formErrors, onCreateError }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Create User</h3>
        <form onSubmit={async (e) => {
          try {
            await onSubmit(e);
          } catch (err) {
            onCreateError?.(err);
          }
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name</label>
            <input className="input mt-1" value={form.full_name} onChange={e=>setForm({...form, full_name: e.target.value})} />
            {formErrors?.full_name && <p className="text-sm text-red-600 mt-1">{formErrors.full_name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Username</label>
            <input className="input mt-1" value={form.username} onChange={e=>setForm({...form, username: e.target.value})} />
            {formErrors?.username && <p className="text-sm text-red-600 mt-1">{formErrors.username}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input type="email" className="input mt-1" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} />
            {formErrors?.email && <p className="text-sm text-red-600 mt-1">{formErrors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input type="password" className="input mt-1" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} />
            {formErrors?.password && <p className="text-sm text-red-600 mt-1">{formErrors.password}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <select className="input mt-1" value={form.role} onChange={e=>setForm({...form, role: e.target.value})}>
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50">
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
